import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMenu } from '../contexts/MenuContext';
import { useTenant } from '../contexts/TenantContext';
import {
    createGuestOrder,
    fetchGuestOrder,
    mergeOrderItems,
    requestCheckout,
    updateGuestOrder,
    GuestOrderSnapshot
} from '../services/orderService';
import './Orders.css';
import { useNavigate } from 'react-router-dom';

type StatusMessage = {
    type: 'success' | 'error';
    text: string;
};

type PaymentMode = 'now' | 'later';
type PaymentMethod = 'cash' | 'pos' | 'bancomat' | 'counter' | 'paypal' | 'stripe';

const Orders: React.FC = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const tenantCtx = useTenant();
    const menu = useMenu();

    if (!tenantCtx) {
        return <div className="orders-page orders-page__empty">{t('order.tenantUnavailable')}</div>;
    }

    if (!menu) {
        return <div className="orders-page orders-page__empty">{t('order.genericError')}</div>;
    }

    const { tenantId } = tenantCtx;
    const {
        cart,
        clearCart,
        incrementItem,
        decrementItem,
        removeFromCart,
        setItemComment,
        activeOrder,
        setActiveOrder
    } = menu;

    const [tableNumber, setTableNumber] = useState(() => activeOrder?.tableNumber ?? '');
    const [customerName, setCustomerName] = useState(() => activeOrder?.customerName ?? '');
    const [message, setMessage] = useState<StatusMessage | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [paymentStep, setPaymentStep] = useState<'idle' | 'choice' | 'method' | 'checkout'>('idle');
    const [checkoutConfirmation, setCheckoutConfirmation] = useState<{ method: string; orderCode: string } | null>(null);
    const navigationTimeout = useRef<number | null>(null);

    const cartOrderItems = useMemo(() => cart.map(item => ({
        productId: item.productId,
        baseProductId: item.baseProductId,
        variantId: item.variantId,
        name: item.name,
        variantName: item.variantName,
        price: item.price,
        quantity: item.quantity,
        comment: item.comment
    })), [cart]);

    const cartTotal = useMemo(() => cart.reduce((sum, item) => sum + item.price * item.quantity, 0), [cart]);
    const cartCount = useMemo(() => cart.reduce((sum, item) => sum + item.quantity, 0), [cart]);
    const activeOrderItems = useMemo(() => activeOrder?.orderItems ?? [], [activeOrder?.orderItems]);
    const paymentMethods = useMemo<{ id: PaymentMethod; label: string; available: boolean; reason?: string }[]>(() => ([
        { id: 'cash', label: t('order.paymentMethods.cash'), available: true },
        { id: 'pos', label: t('order.paymentMethods.pos'), available: true },
        { id: 'bancomat', label: t('order.paymentMethods.bancomat'), available: true },
        { id: 'counter', label: t('order.paymentMethods.counter', { defaultValue: 'Al banco' }), available: true },
        {
            id: 'paypal',
            label: t('order.paymentMethods.paypal'),
            available: false,
            reason: t('order.paymentDigitalUnavailable', {
                method: 'PayPal',
                defaultValue: 'Pagamento con PayPal non è ancora disponibile. Seleziona un altro metodo.'
            })
        },
        {
            id: 'stripe',
            label: t('order.paymentMethods.stripe'),
            available: false,
            reason: t('order.paymentDigitalUnavailable', {
                method: 'Stripe',
                defaultValue: 'Pagamento con Stripe non è ancora disponibile. Seleziona un altro metodo.'
            })
        }
    ]), [t]);

    const resolveStatusLabel = useCallback((status?: string) => {
        if (!status) {
            const fallback = t('order.status.OPEN', { defaultValue: 'In preparazione' });
            return fallback;
        }
        const key = `order.status.${status}`;
        const translated = t(key, { defaultValue: status });
        return translated || status;
    }, [t]);

    const persistActiveOrder = useCallback((snapshot: GuestOrderSnapshot) => {
        setActiveOrder({
            orderCode: snapshot.orderCode,
            tableNumber: snapshot.tableNumber,
            status: snapshot.status,
            isPaid: snapshot.isPaid,
            totalAmount: snapshot.totalAmount,
            customerName: snapshot.customerName,
            orderItems: snapshot.orderItems,
            lastUpdatedAt: snapshot.lastUpdatedAt
        });
    }, [setActiveOrder]);

    useEffect(() => {
        if (activeOrder?.tableNumber && !tableNumber) {
            setTableNumber(activeOrder.tableNumber);
        }
        if (activeOrder?.customerName && !customerName) {
            setCustomerName(activeOrder.customerName);
        }
    }, [activeOrder?.tableNumber, activeOrder?.customerName, tableNumber, customerName]);

    useEffect(() => {
        if (!tenantId || !activeOrder?.orderCode) return;
        let cancelled = false;

        fetchGuestOrder(tenantId, activeOrder.orderCode)
            .then(snapshot => {
                if (cancelled) return;
                if (!snapshot) {
                    setActiveOrder(null);
                    return;
                }
                persistActiveOrder(snapshot);
            })
            .catch(err => {
                if (cancelled) return;
                console.warn('Impossibile aggiornare lo stato dell\'ordine attivo', err);
            });

        return () => {
            cancelled = true;
        };
    }, [tenantId, activeOrder?.orderCode, persistActiveOrder, setActiveOrder]);

    useEffect(() => {
        return () => {
            if (navigationTimeout.current !== null) {
                window.clearTimeout(navigationTimeout.current);
                navigationTimeout.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (!message) return;
        const timeout = window.setTimeout(() => setMessage(null), 5000);
        return () => window.clearTimeout(timeout);
    }, [message]);

    const formatDateTime = (value?: string) => {
        if (!value) return '';
        try {
            const date = new Date(value);
            if (Number.isNaN(date.getTime())) return '';
            return new Intl.DateTimeFormat(i18n.language, {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            }).format(date);
        } catch (error) {
            return '';
        }
    };

    const ensureTenant = () => {
        if (!tenantId) {
            setMessage({ type: 'error', text: t('order.tenantUnavailable') });
            return false;
        }
        return true;
    };

    const closePaymentFlow = useCallback(() => {
        if (submitting) return;
        setPaymentStep('idle');
    }, [submitting]);

    const handleSubmit = () => {
        setMessage(null);
        if (!ensureTenant()) return;
        if (!tableNumber.trim()) {
            setMessage({ type: 'error', text: t('order.errorTableRequired') });
            return;
        }
        if (!activeOrder?.orderCode && cartOrderItems.length === 0) {
            setMessage({ type: 'error', text: t('order.errorCartEmpty') });
            return;
        }

        setPaymentStep('choice');
    };

    const submitOrder = async (mode: PaymentMode, method?: PaymentMethod) => {
        if (!ensureTenant()) return;
        if (!tenantId) return;

        setPaymentStep('idle');
        setSubmitting(true);

        try {
            let snapshot: GuestOrderSnapshot;
            const trimmedTable = tableNumber.trim();
            const trimmedName = customerName.trim() || undefined;

            // PASSO 1: Crea sempre l'ordine come OPEN (non pagato)
            if (activeOrder?.orderCode) {
                const mergedItems = mergeOrderItems(activeOrder.orderItems ?? [], cartOrderItems);
                const mergedTotal = mergedItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
                snapshot = await updateGuestOrder(tenantId, activeOrder.orderCode, {
                    tableNumber: trimmedTable,
                    customerName: trimmedName,
                    status: 'OPEN',
                    items: mergedItems,
                    totalAmount: mergedTotal,
                    isPaid: false // Sempre false, pagamento gestito separatamente
                });
            } else {
                snapshot = await createGuestOrder(tenantId, {
                    tableNumber: trimmedTable,
                    customerName: trimmedName,
                    status: 'OPEN',
                    items: cartOrderItems,
                    totalAmount: cartTotal,
                    isPaid: false // Sempre false, pagamento gestito separatamente
                });
            }

            // PASSO 2: Se "pago adesso", richiedi subito il checkout con metodo scelto
            if (mode === 'now' && method) {
                await requestCheckout(tenantId, snapshot.orderCode, method);

                // Mostra conferma con istruzioni per "pago adesso"
                setCheckoutConfirmation({
                    method: method,
                    orderCode: snapshot.orderCode
                });

                persistActiveOrder({
                    orderCode: snapshot.orderCode,
                    tableNumber: snapshot.tableNumber,
                    customerName: snapshot.customerName,
                    status: 'CHECKOUT_REQUESTED',
                    orderItems: snapshot.orderItems,
                    totalAmount: snapshot.totalAmount,
                    isPaid: false,
                    lastUpdatedAt: new Date().toISOString()
                });
                clearCart();

                // Non tornare al menu automaticamente, aspetta click su conferma
                return;
            }

            // PASSO 3: Se "pago dopo", mostra solo messaggio successo
            const successKey = activeOrder?.orderCode
                ? 'order.successUpdateLater'
                : 'order.successCreateLater';

            setMessage({
                type: 'success',
                text: t(successKey, {
                    code: snapshot.orderCode
                })
            });

            persistActiveOrder(snapshot);
            clearCart();

            if (navigationTimeout.current !== null) {
                window.clearTimeout(navigationTimeout.current);
                navigationTimeout.current = null;
            }
            navigationTimeout.current = window.setTimeout(() => {
                navigate('/menu');
                navigationTimeout.current = null;
            }, 1600);
        } catch (error) {
            const text = error instanceof Error ? error.message : t('order.genericError');
            setMessage({ type: 'error', text });
        } finally {
            setSubmitting(false);
        }
    };

    const handlePaymentLater = () => {
        void submitOrder('later');
    };

    const handlePaymentNow = () => {
        setPaymentStep('method');
    };

    const handlePaymentMethodSelect = (method: PaymentMethod) => {
        const selected = paymentMethods.find(entry => entry.id === method);
        if (!selected?.available) {
            setMessage({
                type: 'error',
                text: selected?.reason || t('order.paymentDigitalUnavailable', {
                    method: method.toUpperCase(),
                    defaultValue: 'Questo metodo di pagamento non è ancora disponibile.'
                })
            });
            return;
        }

        // Se siamo in modalità checkout (richiesta pagamento), usa requestCheckout
        if (paymentStep === 'checkout') {
            void handleConfirmCheckout(method);
        } else {
            // Altrimenti è un pagamento immediato durante la creazione ordine
            void submitOrder('now', method);
        }
    };

    const handleOpenCheckoutFlow = () => {
        if (!activeOrder?.orderCode) return;
        setPaymentStep('checkout');
    };

    const handleConfirmCheckout = async (paymentMethod: PaymentMethod) => {
        setMessage(null);
        if (!ensureTenant() || !activeOrder?.orderCode) return;

        const selected = paymentMethods.find(entry => entry.id === paymentMethod);
        if (!selected?.available) {
            setMessage({
                type: 'error',
                text: selected?.reason || t('order.paymentDigitalUnavailable', {
                    method: paymentMethod.toUpperCase(),
                    defaultValue: 'Questo metodo di pagamento non è ancora disponibile.'
                })
            });
            return;
        }

        setSubmitting(true);
        try {
            await requestCheckout(tenantId!, activeOrder.orderCode, paymentMethod);

            // Mostra conferma con istruzioni
            setCheckoutConfirmation({
                method: paymentMethod,
                orderCode: activeOrder.orderCode
            });

            // Aggiorna lo stato locale
            persistActiveOrder({
                orderCode: activeOrder.orderCode,
                tableNumber: activeOrder.tableNumber,
                customerName: activeOrder.customerName,
                status: 'CHECKOUT_REQUESTED',
                orderItems: activeOrder.orderItems ?? [],
                totalAmount: activeOrder.totalAmount ?? 0,
                isPaid: activeOrder.isPaid ?? false,
                lastUpdatedAt: new Date().toISOString()
            });
        } catch (error) {
            const text = error instanceof Error ? error.message : t('order.genericError');
            setMessage({ type: 'error', text });
        } finally {
            setSubmitting(false);
        }
    };

    const handleCloseCheckoutConfirmation = () => {
        setCheckoutConfirmation(null);
        setActiveOrder(null);
        clearCart();
        navigate('/menu');
    };

    const handleCloseOrder = () => {
        setActiveOrder(null);
        clearCart();
        setMessage(null);
    };

    const disableSubmit = submitting || !tenantId || !tableNumber.trim() || (!activeOrder?.orderCode && cartOrderItems.length === 0);

    return (
        <div className="orders-page">
            <header className="orders-header">
                <button
                    type="button"
                    className="orders-back"
                    onClick={() => navigate('/menu')}
                >
                    <span className="orders-back__icon" aria-hidden="true">←</span>
                    <span>{t('order.backToMenu')}</span>
                </button>
                <div className="orders-header__text">
                    <h1>{t('order.checkoutTitle')}</h1>
                    <p>{t('order.checkoutSubtitle')}</p>
                </div>
            </header>

            {message && (
                <div className={`orders-toast orders-toast--${message.type}`} role="status">
                    {message.text}
                </div>
            )}

            {activeOrder?.orderCode && (
                <section className="orders-card orders-card__active">
                    <h2>{t('order.activeOrderTitle')}</h2>
                    <div className="orders-active-info">
                        <span>{t('order.orderCodeLabel', { code: activeOrder.orderCode })}</span>
                        <span>{t('order.orderStatusLabel', { status: resolveStatusLabel(activeOrder.status || 'OPEN') })}</span>
                        <span>{t('order.activeOrderTotal', { total: (activeOrder.totalAmount ?? 0).toFixed(2) })}</span>
                        {activeOrder.lastUpdatedAt && (
                            <span>{t('order.activeOrderUpdated', { value: formatDateTime(activeOrder.lastUpdatedAt) })}</span>
                        )}
                    </div>
                    <p className="orders-code-hint">{t('order.codeHelp')}</p>
                </section>
            )}

            {activeOrder?.orderCode && (
                <section className="orders-card">
                    <div className="orders-card__header">
                        <h2>{t('order.activeItemsTitle')}</h2>
                        <span className="orders-card__chips">€{(activeOrder.totalAmount ?? 0).toFixed(2)}</span>
                    </div>
                    {activeOrderItems.length === 0 ? (
                        <p className="orders-empty">{t('order.activeItemsEmpty')}</p>
                    ) : (
                        <ul className="orders-history-list">
                            {activeOrderItems.map(item => (
                                <li key={item.productId} className="orders-history-item">
                                    <div className="orders-history-item__left">
                                        <span className="orders-history-item__qty">{item.quantity}×</span>
                                        <div className="orders-history-item__info">
                                            <span className="orders-history-item__name">
                                                {item.name}
                                                {item.variantName ? (
                                                    <span className="orders-history-item__variant"> · Extra: {item.variantName}</span>
                                                ) : null}
                                            </span>
                                            {item.comment ? (
                                                <span className="orders-history-item__note">{item.comment}</span>
                                            ) : null}
                                        </div>
                                    </div>
                                    <span className="orders-history-item__price">€{(item.price * item.quantity).toFixed(2)}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            )}

            <section className="orders-card">
                <div className="orders-card__header">
                    <h2>{t('order.cartSectionTitle')}</h2>
                    <span className="orders-card__chips">{t('order.itemsCount', { count: cartCount })}</span>
                </div>

                {cart.length === 0 ? (
                    <p className="orders-empty">{t('order.emptyCart')}</p>
                ) : (
                    <ul className="orders-list">
                        {cart.map(item => (
                            <li key={item.productId} className="orders-item">
                                <div className="orders-item__info">
                                    <div className="orders-item__identity">
                                        <strong>{item.name}</strong>
                                        {item.variantName && (
                                            <span className="orders-item__variant">Extra: {item.variantName}</span>
                                        )}
                                    </div>
                                    <span className="orders-item__price">€{item.price.toFixed(2)}</span>
                                </div>
                                <div className="orders-item__actions">
                                    <button
                                        type="button"
                                        onClick={() => decrementItem(item.productId)}
                                        disabled={submitting}
                                    >
                                        −
                                    </button>
                                    <span>{item.quantity}</span>
                                    <button
                                        type="button"
                                        onClick={() => incrementItem(item.productId)}
                                        disabled={submitting}
                                    >
                                        +
                                    </button>
                                    <button
                                        type="button"
                                        className="orders-item__remove"
                                        onClick={() => removeFromCart(item.productId)}
                                        disabled={submitting}
                                        aria-label={t('order.removeItem')}
                                        title={t('order.removeItem')}
                                    >
                                        <span className="orders-item__remove-icon" aria-hidden="true">🗑</span>
                                    </button>
                                </div>
                                <textarea
                                    className="orders-item__note"
                                    placeholder={t('order.commentPlaceholder')}
                                    value={item.comment || ''}
                                    onChange={(event) => setItemComment(item.productId, event.target.value)}
                                    disabled={submitting}
                                />
                            </li>
                        ))}
                    </ul>
                )}

                <div className="orders-summary">
                    <span>{t('order.summaryTotal')}</span>
                    <strong>€{cartTotal.toFixed(2)}</strong>
                </div>
                <p className="orders-hint">{t('order.addMoreNotice')}</p>
            </section>

            <section className="orders-card orders-card__form">
                <h2>{t('order.detailsTitle')}</h2>
                <label className="orders-field">
                    <span>{t('order.tableNumberLabel')}</span>
                    <input
                        type="text"
                        value={tableNumber}
                        onChange={(event) => setTableNumber(event.target.value)}
                        placeholder={t('order.tableNumberPlaceholder')}
                        disabled={submitting}
                    />
                </label>

                <label className="orders-field">
                    <span>{t('order.customerNameLabel')}</span>
                    <input
                        type="text"
                        value={customerName}
                        onChange={(event) => setCustomerName(event.target.value)}
                        placeholder={t('order.customerNamePlaceholder')}
                        disabled={submitting}
                    />
                </label>
            </section>

            <div className="orders-actions">
                <button
                    type="button"
                    className="orders-primary"
                    onClick={handleSubmit}
                    disabled={disableSubmit}
                >
                    {activeOrder?.orderCode ? t('order.updateOrder') : t('order.submitOrder')}
                </button>

                {activeOrder?.orderCode && (
                    <>
                        <button
                            type="button"
                            className="orders-secondary"
                            onClick={handleOpenCheckoutFlow}
                            disabled={submitting}
                        >
                            {t('order.requestBill')}
                        </button>
                        <button
                            type="button"
                            className="orders-tertiary"
                            onClick={handleCloseOrder}
                            disabled={submitting}
                        >
                            {t('order.closeOrder')}
                        </button>
                    </>
                )}
            </div>

            {paymentStep === 'choice' && (
                <div className="orders-modal">
                    <div className="orders-modal__overlay" onClick={closePaymentFlow} />
                    <div className="orders-modal__dialog">
                        <button
                            type="button"
                            className="orders-modal__close"
                            onClick={closePaymentFlow}
                            aria-label={t('order.closeModal')}
                        >
                            ✕
                        </button>
                        <h3>{t('order.paymentChoiceTitle')}</h3>
                        <p>{t('order.paymentChoiceDescription')}</p>
                        <div className="orders-modal__options">
                            <button
                                type="button"
                                className="orders-modal__option"
                                onClick={handlePaymentNow}
                                disabled={submitting}
                            >
                                <span className="orders-modal__option-title">{t('order.paymentChoiceNow')}</span>
                                <span className="orders-modal__option-hint">{t('order.paymentChoiceNowHint')}</span>
                            </button>
                            <button
                                type="button"
                                className="orders-modal__option orders-modal__option--ghost"
                                onClick={handlePaymentLater}
                                disabled={submitting}
                            >
                                <span className="orders-modal__option-title">{t('order.paymentChoiceLater')}</span>
                                <span className="orders-modal__option-hint">{t('order.paymentChoiceLaterHint')}</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {paymentStep === 'method' && (
                <div className="orders-modal">
                    <div className="orders-modal__overlay" onClick={closePaymentFlow} />
                    <div className="orders-modal__dialog">
                        <button
                            type="button"
                            className="orders-modal__close"
                            onClick={closePaymentFlow}
                            aria-label={t('order.closeModal')}
                        >
                            ✕
                        </button>
                        <h3>{t('order.paymentMethodTitle')}</h3>
                        <p>{t('order.paymentMethodDescription')}</p>
                        <div className="orders-modal__grid">
                            {paymentMethods.map(method => (
                                <button
                                    key={method.id}
                                    type="button"
                                    className="orders-modal__method"
                                    onClick={() => handlePaymentMethodSelect(method.id)}
                                    disabled={submitting || !method.available}
                                >
                                    {method.label}
                                    {!method.available && method.reason && (
                                        <small>{method.reason}</small>
                                    )}
                                </button>
                            ))}
                        </div>
                        <div className="orders-modal__footer">
                            <button
                                type="button"
                                className="orders-modal__back"
                                onClick={() => setPaymentStep('choice')}
                                disabled={submitting}
                            >
                                {t('order.paymentMethodBack')}
                            </button>
                            <span className="orders-modal__disclaimer">{t('order.paymentMethodDisclaimer')}</span>
                        </div>
                    </div>
                </div>
            )}

            {paymentStep === 'checkout' && (
                <div className="orders-modal">
                    <div className="orders-modal__overlay" onClick={closePaymentFlow} />
                    <div className="orders-modal__dialog">
                        <button
                            type="button"
                            className="orders-modal__close"
                            onClick={closePaymentFlow}
                            aria-label={t('order.closeModal')}
                        >
                            ✕
                        </button>
                        <h3>{t('order.checkoutMethodTitle', { defaultValue: 'Come vuoi pagare?' })}</h3>
                        <p>{t('order.checkoutMethodDescription', { defaultValue: 'Seleziona il metodo di pagamento che preferisci. Lo staff porterà il conto al tuo tavolo.' })}</p>
                        <div className="orders-modal__grid">
                            {paymentMethods.filter(m => m.available).map(method => (
                                <button
                                    key={method.id}
                                    type="button"
                                    className="orders-modal__method"
                                    onClick={() => handlePaymentMethodSelect(method.id)}
                                    disabled={submitting}
                                >
                                    {method.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {checkoutConfirmation && (
                <div className="orders-modal">
                    <div className="orders-modal__overlay" onClick={handleCloseCheckoutConfirmation} />
                    <div className="orders-modal__dialog orders-modal__dialog--success">
                        <div className="orders-modal__success-icon">✓</div>
                        <h3>{t('order.checkoutConfirmTitle', { defaultValue: 'Richiesta inviata!' })}</h3>
                        <div className="orders-modal__instructions">
                            <p>
                                <strong>{t('order.checkoutConfirmMessage', { defaultValue: 'Il personale è stato avvisato della tua richiesta di pagamento.' })}</strong>
                            </p>
                            <p>
                                {t('order.checkoutConfirmOrderCode', {
                                    defaultValue: 'Codice ordine: {{code}}',
                                    code: checkoutConfirmation.orderCode
                                })}
                            </p>
                            <div className="orders-modal__payment-info">
                                <p className="orders-modal__payment-method">
                                    💳 <strong>{t(`order.paymentMethods.${checkoutConfirmation.method}`)}</strong>
                                </p>
                                <div className="orders-modal__instructions-list">
                                    {checkoutConfirmation.method === 'counter' ? (
                                        <>
                                            <p>🏪 {t('order.checkoutInstructionCounter1', { defaultValue: 'Recati al banco per effettuare il pagamento' })}</p>
                                            <p>📋 {t('order.checkoutInstructionCounter2', { defaultValue: 'Comunica il codice ordine al personale' })}</p>
                                            <p>🧾 {t('order.checkoutInstructionCounter3', { defaultValue: 'Riceverai lo scontrino fiscale dopo il pagamento' })}</p>
                                        </>
                                    ) : (
                                        <>
                                            <p>📋 {t('order.checkoutInstruction1', { defaultValue: 'Lo staff porterà il pre-scontrino al tuo tavolo' })}</p>
                                            {checkoutConfirmation.method === 'cash' && (
                                                <p>💵 {t('order.checkoutInstructionCash', { defaultValue: 'Prepara i contanti per facilitare il pagamento' })}</p>
                                            )}
                                            {(checkoutConfirmation.method === 'pos' || checkoutConfirmation.method === 'bancomat') && (
                                                <p>💳 {t('order.checkoutInstructionCard', { defaultValue: 'Lo staff porterà il POS per il pagamento con carta' })}</p>
                                            )}
                                            <p>🧾 {t('order.checkoutInstruction3', { defaultValue: 'Riceverai lo scontrino fiscale dopo il pagamento' })}</p>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="orders-modal__confirm"
                            onClick={handleCloseCheckoutConfirmation}
                        >
                            {t('order.checkoutConfirmButton', { defaultValue: 'Ho capito, torna al menu' })}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Orders;